import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { activityApi } from '../api/activityApi';
import { useAuthStore } from '../store/authStore';
import type { ActivityEvent } from '../types/activity';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

const iconMap: Record<string, string> = {
  attendance_alert: '⚠️',
  payment_received: '💰',
  payment_debt: '📉',
  athlete_added: '👤',
  athlete_moved: '🔄',
  injury_reported: '🏥',
  achievement: '🏆',
  note_added: '📝',
  event_upcoming: '📅',
  ai_insight: '🤖',
};

const ActivityCard: React.FC<{ event: ActivityEvent }> = ({ event }) => {
  const icon = iconMap[event.event_type] || '📌';
  const relativeTime = formatDistanceToNow(new Date(event.created_at), {
    addSuffix: true,
    locale: ru,
  });

  const isSevere = event.severity === 'critical' || event.severity === 'warning';

  return (
    <div
      className="p-3 rounded-xl relative overflow-hidden"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        boxShadow: 'var(--card-shadow)',
      }}
    >
      {isSevere && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundColor: event.severity === 'critical' ? 'var(--danger)' : 'var(--accent)',
            opacity: 0.08,
          }}
        />
      )}
      <div className="flex items-start gap-3 relative">
        <span className="text-2xl">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-xs" style={{ color: 'var(--text-hint)' }}>{relativeTime}</div>
          <div className="font-medium mt-1" style={{ color: 'var(--text-primary)' }}>{event.title}</div>
          {event.description && (
            <div className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              {event.description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ActivityFeed: React.FC = () => {
  const currentAssistantId = useAuthStore(state => state.currentAssistantId);

  const { data: events, isLoading } = useQuery({
    queryKey: ['activity-feed', currentAssistantId],
    queryFn: () => activityApi.getByAssistant(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  if (!currentAssistantId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-sm text-gray-500 dark:text-gray-400">Загрузка...</div>
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="p-4">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Пока нет событий
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {events.map(event => (
        <ActivityCard key={event.id} event={event} />
      ))}
    </div>
  );
};
